import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Review {
  id: string;
  rating: number;
  review_text: string;
  created_at: string;
  profiles: {
    first_name: string;
    last_name: string;
  };
}

interface MentorReviewsProps {
  mentorId: string;
  averageRating: number;
  totalReviews: number;
}

export const MentorReviews = ({ mentorId, averageRating, totalReviews }: MentorReviewsProps) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, [mentorId]);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('session_reviews')
        .select(`
          id,
          rating,
          review_text,
          created_at,
          student_id
        `)
        .eq('mentor_id', mentorId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      
      // Get student names separately due to foreign key constraints
      const reviewsWithProfiles = await Promise.all(
        (data || []).map(async (review) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('user_id', review.student_id)
            .single();
          
          return {
            ...review,
            profiles: profile || { first_name: 'Anonymous', last_name: 'Student' }
          };
        })
      );
      
      setReviews(reviewsWithProfiles);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            Loading reviews...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Student Reviews</span>
          <div className="flex items-center space-x-2">
            {renderStars(Math.round(averageRating))}
            <span className="text-lg font-bold">{averageRating.toFixed(1)}</span>
            <span className="text-sm text-muted-foreground">
              ({totalReviews} review{totalReviews !== 1 ? 's' : ''})
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {reviews.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No reviews yet. Be the first to book a session!
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="border-b border-border last:border-b-0 pb-4 last:pb-0"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                      {getInitials(review.profiles.first_name, review.profiles.last_name)}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {review.profiles.first_name} {review.profiles.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(review.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {renderStars(review.rating)}
                    <Badge variant="secondary" className="text-xs">
                      {review.rating}/5
                    </Badge>
                  </div>
                </div>
                
                {review.review_text && (
                  <p className="text-sm text-muted-foreground leading-relaxed ml-11">
                    "{review.review_text}"
                  </p>
                )}
              </div>
            ))}
            
            {totalReviews > 10 && (
              <div className="text-center pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing 10 of {totalReviews} reviews
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};